import { Fragment, useMemo } from 'react';
import styled from 'styled-components';
import { Input } from 'semantic-ui-react';
import { CenterDropdownStyle, NormalSliderCheckbox, ShadowBox } from '../../elements';
import { NumberMaskInput } from '../../elements/CustomMaskInput';
import { CommonUtility } from '../../utility';

const MiniInput = styled(Input)`
  width: 100px;
  input {
    padding-left: 20px !important;
  }
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
`;

const FieldContainer = styled.div`
  &.active {
    background: ${({ theme }) => theme.colors.box};
  }
  padding: 7px;
  border-radius: 5px;
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  margin-top: 20px;
`;

export function RateCardFields({ metadata, userValue, onValueChange }) {
  return (
    <CategoriesContainer>
      {metadata?.categories?.map(category => (
        <CategoryRenderer
          category={category}
          userValue={userValue}
          onValueChange={onValueChange}
        />
      ))}
    </CategoriesContainer>
  );
}

function FrequencyDropdown({ userValue, frequencyOptions, onValueChange }) {
  const options = useMemo(
    () =>
      frequencyOptions?.map(x => ({
        text: x,
        value: x,
        key: x,
      })),
    [frequencyOptions],
  );
  return (
    <CenterDropdownStyle
      options={options}
      selection
      value={userValue.frequency}
      onChange={(e, { value }) => onValueChange('frequency', value)}
      className="mute-dropdown"
    />
  );
}

const maskOptions = {
  prefix: '',
  allowDecimal: true,
  allowNegative: false,
  allowLeadingZeroes: false,
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
  decimalLimit: 5,
};

const Icon = styled.div`
  position: absolute;
  left: 10px;
  top: 9.5px;
  color: ${({ theme }) => theme.colors.grey};
  pointer-events: none;
`;

function RateInput({ userValue, onValueChange }) {
  return (
    <MiniInput>
      <Icon className="icon">$</Icon>
      <NumberMaskInput
        value={userValue.rate || ''}
        onChange={value => onValueChange('rate', value)}
        onKeyPress={e => {
          e.key === 'Enter' && e.preventDefault();
        }}
        maskOptions={maskOptions}
      />
    </MiniInput>
  );
}

function BillDropdown({ userValue, billingOptions, onValueChange }) {
  const options = useMemo(
    () =>
      billingOptions?.map(x => ({
        text: x,
        value: x,
        key: x,
      })),
    [billingOptions],
  );
  return (
    <CenterDropdownStyle
      options={options}
      selection
      value={userValue.billingUnit}
      onChange={(e, { value }) => onValueChange('billingUnit', value)}
      className="mute-dropdown"
    />
  );
}

const InputsContainer = styled.div`
  display: flex;
  column-gap: 10px;
`;

export function CategoryRenderer({ category, userValue, onValueChange }) {
  return (
    <ShadowBox className="row">
      <div className="col-3 pl-0">
        <h3>{category.name}</h3>
      </div>
      <FieldsContainer className="col-9 pr-0">
        {category.services.map(service => (
          <ServiceRenderer
            service={service}
            userValue={
              userValue?.[category.name]?.[service.uniqueName || service.name] || {}
            }
            fullUserValue={userValue}
            onValueChange={(keyName, value) =>
              onValueChange(
                category.name,
                service.uniqueName || service.name,
                keyName,
                value,
              )
            }
          />
        ))}
      </FieldsContainer>
    </ShadowBox>
  );
}

function ServiceRenderer({ service, userValue, onValueChange, fullUserValue }) {
  if (service.showConditionally) {
    if (
      fullUserValue?.[service.condition.categoryKey]?.[service.condition.serviceKey]?.[
        service.condition.fieldKey
      ] !== service.condition.value
    ) {
      return <></>;
    }
  }

  return (
    <Fragment key={service.uniqueName || service.name}>
      <FieldContainer
        className={`d-flex align-items-center justify-content-between ${
          userValue.enabled ? 'active' : 'not-acitve'
        }`}
      >
        <div className="d-flex align-items-center">
          {service.showToggle !== false && (
            <NormalSliderCheckbox
              onChange={(e, { checked }) => onValueChange('enabled', checked)}
              checked={userValue.enabled}
              className="mr-2"
            />
          )}
          {service.showTitle !== false && <b>{service.name}</b>}
        </div>
        <InputsContainer className="d-flex align-items-center">
          {CommonUtility.isValidArray(service?.frequency) && (
            <FrequencyDropdown
              frequencyOptions={service?.frequency}
              userValue={userValue}
              onValueChange={onValueChange}
            />
          )}
          {CommonUtility.isValidArray(service?.billingOptions) && (
            <BillDropdown
              billingOptions={service?.billingOptions}
              userValue={userValue}
              onValueChange={onValueChange}
            />
          )}
          <RateInput userValue={userValue} onValueChange={onValueChange} />
        </InputsContainer>
      </FieldContainer>
    </Fragment>
  );
}

export const prepareRateCategories = (userValue, metadata) => {
  const fields = [];
  const parsedUserValue = JSON.parse(JSON.stringify(userValue));

  Object.keys(parsedUserValue).forEach(categoryName => {
    const categoryObject = {
      name: categoryName,
      services: [],
    };

    const userCategory = metadata.categories.find(cat => cat.name === categoryName);

    Object.values(parsedUserValue[categoryName]).forEach(orgService => {
      let metaService = null;
      const service = orgService;
      if (service.uniqueName) {
        metaService = userCategory.services.find(
          x => x.uniqueName === service.uniqueName,
        );

        if (CommonUtility.isValidObject(parsedUserValue[categoryName][service.name])) {
          service.enabled = parsedUserValue[categoryName][service.name]?.enabled;
        }
      } else {
        metaService = userCategory.services.find(x => x.name === service.name);
      }

      if (metaService.showConditionally) {
        if (
          parsedUserValue?.[metaService.condition.categoryKey]?.[
            metaService.condition.serviceKey
          ]?.[metaService.condition.fieldKey] === metaService.condition.value
        ) {
          delete service.uniqueName;
          delete service._id;
          if (Object.keys(service).length > 1) {
            categoryObject.services.push(service);
          }
        }
      } else {
        delete service.uniqueName;
        delete service._id;
        if (Object.keys(service).length > 1) {
          categoryObject.services.push(service);
        }
      }
    });
    fields.push(categoryObject);
  });
  return fields;
};

const checkConditionIsMet = (service, userRate) => {
  const category = userRate.categories.find(
    x => x.name === service.condition.categoryKey,
  );
  if (category) {
    const userService = category.services.find(
      x => x.name === service.condition.serviceKey,
    );
    if (userService) {
      return userService[service.condition.fieldKey] === service.condition.value;
    }
  }
  return false;
};

export const populateRate = (metadata, rate) => {
  const value = {};
  metadata.categories.forEach(category => {
    const userCategory = rate?.categories?.find(cat => cat.name === category.name);

    category.services.forEach(service => {
      const userService = userCategory?.services?.find(s => {
        if (CommonUtility.isValidArray(service.billingOptions)) {
          return (
            s.name === service.name && service.billingOptions.includes(s.billingUnit)
          );
        }
        return s.name === service.name;
      });

      let similarParent = [];

      if (service.showConditionally) {
        if (checkConditionIsMet(service, rate)) {
          similarParent = userCategory?.services?.filter(x => {
            if (x.showConditionally) {
              return checkConditionIsMet(x, rate);
            }
            return x.name === service.name;
          });
        }
      } else {
        similarParent = userCategory?.services?.filter(x => {
          if (x.showConditionally) {
            return checkConditionIsMet(x, rate);
          }
          return x.name === service.name;
        });
      }

      let enabled = !!userService?.enabled;

      if (similarParent?.length > 1) {
        enabled = similarParent.every(x => x.enabled);
      }

      const serviceValue = {
        name: service.name,
        ...(userService || {}),
        enabled,
      };

      if (service.uniqueName) {
        serviceValue.uniqueName = service.uniqueName;
      }

      if (value[category.name]) {
        value[category.name] = {
          ...value[category.name],
          [service.uniqueName || service.name]: serviceValue,
        };
      } else {
        value[category.name] = {
          [service.uniqueName || service.name]: serviceValue,
        };
      }
    });
  });
  return value;
};
